import { Config } from '../../../environments/environment';

/**
 * Where to send a signer whose job was sent under an older API version, or null when there is
 * nowhere to send them.
 *
 * A job keeps the API version it was sent with, and the backend of this version refuses to sign it.
 * Such a job is finished by the legacy app (WEB_APP_URL_LEGACY), which serves the same paths. An
 * empty legacy URL means this environment has no legacy app. A target that resolves to the page the
 * signer is already on would reload that page forever, so it is treated the same way. In both cases
 * the caller shows the incompatible-version message instead of redirecting.
 */
export function legacyRedirectUrl(path: string = window.location.pathname): string | null {
  const legacyBase = (Config.WEB_APP_URL_LEGACY || '').trim();
  if (!legacyBase) {
    return null;
  }

  let target: URL;
  try {
    target = new URL(legacyBase.replace(/\/+$/, '') + path, window.location.href);
  } catch {
    return null;
  }

  if (target.origin === window.location.origin && target.pathname === window.location.pathname) {
    return null;
  }
  return target.href;
}

/**
 * True when a job sent under an older API version must be finished on the legacy app. Only a job
 * that is still in progress needs signing; a completed or rejected one is only read, and this app
 * can show it.
 */
export function needsLegacyApp(job: { apiVersion?: number | null; status?: string | null }): boolean {
  return !!job.apiVersion && job.apiVersion < Config.API_VERSION && job.status === 'IN_PROGRESS';
}
